import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { resolveLeadOwnerName } from '../utils/leadOwner';
import { leadPath } from '../utils/crmEnv';

const statusLabels = {
    new: 'New',
    contacted: 'Contacted',
    qualified: 'Qualified',
    unqualified: 'Unqualified',
    converted: 'Converted',
};

const sourceLabels = {
    website: 'Website',
    referral: 'Referral',
    cold_call: 'Cold Call',
    advertisement: 'Advertisement',
    social_media: 'Social Media',
    walk_in: 'Walk-in',
    other: 'Other',
};

const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'unqualified', label: 'Unqualified' },
    { value: 'converted', label: 'Converted' },
];

const LEAD_TABLE_COLUMN_DEFS = [
    { key: 'name', label: 'Name' },
    { key: 'leadId', label: 'Lead ID' },
    { key: 'nextFollowUp', label: 'Next Follow-up' },
    { key: 'lastTouch', label: 'Last Touch' },
    { key: 'intent', label: 'Intent' },
    { key: 'location', label: 'Location' },
    { key: 'deals', label: 'Deals' },
    { key: 'assignedTo', label: 'Assigned To' },
    { key: 'status', label: 'Status' },
    { key: 'score', label: 'Score' },
    { key: 'quickActions', label: 'Quick Actions' },
];

const LEAD_TABLE_COLUMN_KEYS = LEAD_TABLE_COLUMN_DEFS.map((column) => column.key);
const LEAD_TABLE_COLUMN_KEY_SET = new Set(LEAD_TABLE_COLUMN_KEYS);
const LEAD_TABLE_COLUMN_WIDTHS = {
    name: 240,
    leadId: 92,
    nextFollowUp: 180,
    lastTouch: 140,
    intent: 120,
    location: 170,
    deals: 140,
    assignedTo: 170,
    status: 120,
    score: 80,
    quickActions: 156,
};

const getLeadTableColumnsStorageKey = (uid) => `crmLeadTableColumns:${uid || 'default'}`;

const normalizeLeadTableColumns = (candidate) => {
    if (!Array.isArray(candidate)) return [...LEAD_TABLE_COLUMN_KEYS];
    const selected = new Set();
    candidate.forEach((key) => {
        if (LEAD_TABLE_COLUMN_KEY_SET.has(key)) selected.add(key);
    });
    const normalized = LEAD_TABLE_COLUMN_KEYS.filter((key) => selected.has(key));
    return normalized.length > 0 ? normalized : [...LEAD_TABLE_COLUMN_KEYS];
};

const loadLeadTableColumns = (uid) => {
    if (typeof window === 'undefined') return [...LEAD_TABLE_COLUMN_KEYS];
    try {
        const raw = window.localStorage.getItem(getLeadTableColumnsStorageKey(uid));
        if (!raw) return [...LEAD_TABLE_COLUMN_KEYS];
        return normalizeLeadTableColumns(JSON.parse(raw));
    } catch {
        return [...LEAD_TABLE_COLUMN_KEYS];
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return '-';
    }
};

const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const formatRelativeTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
};

const getFollowUpStatus = (lead) => {
    const now = new Date();
    const due = lead?.nextFollowUpAt ? new Date(lead.nextFollowUpAt) : null;
    const openCount = Number(lead?.openCount || 0);
    const overdueCount = Number(lead?.overdueCount || 0);

    if (due && !Number.isNaN(due.getTime())) {
        if (due.getTime() < now.getTime()) return { label: 'Overdue', className: 'crm-pill-danger', display: formatDateTime(lead.nextFollowUpAt) };
        return { label: 'Coming up', className: 'crm-pill-warning', display: formatDateTime(lead.nextFollowUpAt) };
    }
    if (overdueCount > 0) return { label: 'Overdue', className: 'crm-pill-danger', display: '-' };
    if (openCount === 0 && overdueCount === 0) {
        return { label: 'Completed', className: 'crm-pill-success', display: 'Everything up to date' };
    }
    return { label: 'Coming up', className: 'crm-pill-warning', display: '-' };
};

const formatStage = (value) => {
    if (!value) return '-';
    return String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const getLastTouchLabel = (lead) => {
    if (!lead?.lastTouchType) return 'Activity';
    return String(lead.lastTouchType).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatLocation = (lead) => {
    if (lead?.locationSummary) return lead.locationSummary;
    const localities = Array.isArray(lead?.localities)
        ? lead.localities
        : typeof lead?.localities === 'string'
            ? lead.localities.split(',').map((item) => item.trim()).filter(Boolean)
            : typeof lead?.locality === 'string'
                ? lead.locality.split(',').map((item) => item.trim()).filter(Boolean)
                : [];
    const locality = localities[0] || '';
    const city = lead?.city || '';
    const pincode = lead?.pincode || '';
    const segments = [];
    if (locality) segments.push(locality);
    if (city) segments.push(city);
    if (pincode) segments.push(pincode);
    return segments.length > 0 ? segments.join(', ') : '-';
};

const normalizeCellText = (value) => String(value ?? '').trim();

function LeadTable({
    leads,
    currentUser,
    assignmentOptions = [],
    selectedIds = new Set(),
    onSelect,
    onSelectAll,
    onEdit,
    onDelete,
    onAssign,
    onStatusChange,
    onAddTask,
    onQuickAction,
}) {
    const history = useHistory();
    const currentUserUid = currentUser?.uid || 'default';
    const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState(() => loadLeadTableColumns(currentUserUid));
    const columnsMenuRef = useRef(null);

    useEffect(() => {
        setVisibleColumns(loadLeadTableColumns(currentUserUid));
    }, [currentUserUid]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.setItem(
                getLeadTableColumnsStorageKey(currentUserUid),
                JSON.stringify(normalizeLeadTableColumns(visibleColumns)),
            );
        } catch {
            // Ignore localStorage failures.
        }
    }, [currentUserUid, visibleColumns]);

    useEffect(() => {
        if (!columnsMenuOpen) return undefined;
        const handlePointerDown = (event) => {
            if (columnsMenuRef.current && !columnsMenuRef.current.contains(event.target)) {
                setColumnsMenuOpen(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === 'Escape') setColumnsMenuOpen(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [columnsMenuOpen]);

    const visibleColumnSet = useMemo(() => new Set(normalizeLeadTableColumns(visibleColumns)), [visibleColumns]);
    const visibleColumnCount = visibleColumnSet.size;
    const tableMinWidth = useMemo(() => (
        36 + LEAD_TABLE_COLUMN_KEYS.reduce((total, key) => (
            visibleColumnSet.has(key) ? total + Number(LEAD_TABLE_COLUMN_WIDTHS[key] || 140) : total
        ), 0)
    ), [visibleColumnSet]);

    const allSelected = leads && leads.length > 0 && leads.every((lead) => selectedIds.has(lead.id));

    const isColumnVisible = (key) => visibleColumnSet.has(key);
    const getColumnStyle = (key, baseStyle) => (
        isColumnVisible(key) ? baseStyle : { ...(baseStyle || {}), display: 'none' }
    );
    const toggleColumn = (key) => {
        setVisibleColumns((prev) => {
            const normalized = normalizeLeadTableColumns(prev);
            const next = new Set(normalized);
            if (next.has(key)) {
                if (next.size === 1) return normalized;
                next.delete(key);
            } else {
                next.add(key);
            }
            return LEAD_TABLE_COLUMN_KEYS.filter((columnKey) => next.has(columnKey));
        });
    };
    const resetColumns = () => {
        setVisibleColumns([...LEAD_TABLE_COLUMN_KEYS]);
    };

    const handleRowClick = (lead) => {
        history.push(leadPath(lead.id));
    };

    const handleStatusChange = (e, leadId) => {
        e.stopPropagation();
        onStatusChange?.(leadId, e.target.value);
    };

    const handleAssignChange = (e, leadId) => {
        e.stopPropagation();
        const nextAssignee = e.target.value;
        if (!nextAssignee) return;
        onAssign?.(leadId, nextAssignee);
    };

    const handleEdit = (e, lead) => {
        e.stopPropagation();
        onEdit?.(lead);
    };

    const handleDelete = (e, lead) => {
        e.stopPropagation();
        onDelete?.(lead.id);
    };

    const handleSelect = (e, leadId) => {
        e.stopPropagation();
        onSelect?.(leadId, e.target.checked);
    };

    const handleSelectAll = (e) => {
        e.stopPropagation();
        const ids = leads.map((lead) => lead.id);
        onSelectAll?.(ids, e.target.checked);
    };

    const handleAddTask = (e, lead) => {
        e.stopPropagation();
        onAddTask?.(lead);
    };

    const handleQuickActionClick = (event, lead, channel) => {
        event.stopPropagation();
        if (typeof onQuickAction !== 'function') {
            return;
        }
        event.preventDefault();
        void onQuickAction(lead, channel);
    };

    const getScoreStyle = (score) => {
        if (score >= 70) {
            return { background: 'var(--crm-success-light)', color: 'var(--crm-success)' };
        }
        if (score >= 40) {
            return { background: 'var(--crm-warning-light)', color: '#b45309' };
        }
        return { background: 'var(--crm-danger-light)', color: 'var(--crm-danger)' };
    };

    if (!leads || leads.length === 0) {
        return (
            <div className="crm-empty-state">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <h3>No leads yet</h3>
                <p>Start adding leads to track your potential customers.</p>
            </div>
        );
    }

    return (
        <div>
            <div
                className="crm-toolbar-actions"
                style={{ justifyContent: 'flex-end', marginBottom: 10, position: 'relative' }}
                ref={columnsMenuRef}
            >
                <button
                    type="button"
                    className="crm-btn crm-btn-secondary crm-btn-sm"
                    onClick={() => setColumnsMenuOpen((prev) => !prev)}
                    aria-haspopup="dialog"
                    aria-expanded={columnsMenuOpen ? 'true' : 'false'}
                >
                    Columns ({visibleColumnCount}/{LEAD_TABLE_COLUMN_KEYS.length})
                </button>
                {columnsMenuOpen && (
                    <div
                        className="crm-card"
                        role="dialog"
                        aria-label="Customize table columns"
                        style={{
                            position: 'absolute',
                            top: 'calc(100% + 8px)',
                            right: 0,
                            width: 260,
                            zIndex: 20,
                            boxShadow: 'var(--crm-shadow-lg)',
                            border: '1px solid var(--crm-gray-200)',
                            background: 'var(--crm-bg)',
                        }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="crm-card-body" style={{ padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--crm-gray-700)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                    Table Columns
                                </div>
                                <button
                                    type="button"
                                    className="crm-btn crm-btn-secondary crm-btn-sm"
                                    style={{ padding: '4px 8px', fontSize: 11 }}
                                    onClick={resetColumns}
                                >
                                    Reset
                                </button>
                            </div>
                            <div style={{ display: 'grid', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                                {LEAD_TABLE_COLUMN_DEFS.map((column) => {
                                    const checked = isColumnVisible(column.key);
                                    const disableUncheck = checked && visibleColumnCount === 1;
                                    return (
                                        <label
                                            key={column.key}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                fontSize: 13,
                                                color: disableUncheck ? 'var(--crm-gray-400)' : 'var(--crm-gray-800)',
                                                cursor: disableUncheck ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                className="crm-checkbox"
                                                checked={checked}
                                                disabled={disableUncheck}
                                                onChange={() => toggleColumn(column.key)}
                                            />
                                            <span>{column.label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--crm-gray-500)' }}>
                                Keep at least one column visible.
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="crm-table-container crm-leads-table-shell">
                <table className="crm-table crm-leads-table" style={{ tableLayout: 'fixed', minWidth: tableMinWidth }}>
                <thead>
                    <tr>
                        <th style={{ width: '36px' }}>
                            <input
                                type="checkbox"
                                className="crm-checkbox"
                                checked={allSelected}
                                onChange={handleSelectAll}
                            />
                        </th>
                        <th style={getColumnStyle('name', { width: LEAD_TABLE_COLUMN_WIDTHS.name })}>Name</th>
                        <th style={getColumnStyle('leadId', { width: LEAD_TABLE_COLUMN_WIDTHS.leadId })}>Lead ID</th>
                        <th style={getColumnStyle('nextFollowUp', { width: LEAD_TABLE_COLUMN_WIDTHS.nextFollowUp })}>Next Follow-up</th>
                        <th style={getColumnStyle('lastTouch', { width: LEAD_TABLE_COLUMN_WIDTHS.lastTouch })}>Last Touch</th>
                        <th style={getColumnStyle('intent', { width: LEAD_TABLE_COLUMN_WIDTHS.intent })}>Intent</th>
                        <th style={getColumnStyle('location', { width: LEAD_TABLE_COLUMN_WIDTHS.location })}>Location</th>
                        <th style={getColumnStyle('deals', { width: LEAD_TABLE_COLUMN_WIDTHS.deals })}>Deals</th>
                        <th style={getColumnStyle('assignedTo', { width: LEAD_TABLE_COLUMN_WIDTHS.assignedTo })}>Assigned To</th>
                        <th style={getColumnStyle('status', { width: LEAD_TABLE_COLUMN_WIDTHS.status })}>Status</th>
                        <th style={getColumnStyle('score', { width: LEAD_TABLE_COLUMN_WIDTHS.score })}>Score</th>
                        <th className="cell-actions" style={getColumnStyle('quickActions', { width: LEAD_TABLE_COLUMN_WIDTHS.quickActions })}>Quick Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {leads.map((lead) => {
                        const displayName = lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || '-';
                        const leadNameCandidates = new Set(
                            [
                                lead.name,
                                displayName,
                                `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
                            ]
                                .map((value) => normalizeCellText(value).toLowerCase())
                                .filter(Boolean)
                        );
                        const sanitizeLeadField = (value, { suppressNameEcho = true } = {}) => {
                            const text = normalizeCellText(value);
                            if (!text) return '-';
                            if (suppressNameEcho && leadNameCandidates.has(text.toLowerCase())) return '-';
                            return text;
                        };
                        const ownerName = resolveLeadOwnerName(lead, currentUser);
                        const assignmentCandidates = [lead.assignedTo, lead.ownerSeatId]
                            .map((value) => String(value || '').trim())
                            .filter(Boolean);
                        const assignmentLookup = new Set();
                        assignmentCandidates.forEach((value) => {
                            if (value.startsWith('seat:')) {
                                assignmentLookup.add(value);
                                assignmentLookup.add(value.replace('seat:', ''));
                                return;
                            }
                            assignmentLookup.add(value);
                            assignmentLookup.add(`seat:${value}`);
                        });
                        const matchedAssignmentBySeat = assignmentOptions.find((option) => (
                            assignmentLookup.has(option.seatUid) || assignmentLookup.has(option.seatId)
                        ));
                        const normalizedAssignedName = String(lead.assignedToName || ownerName || '')
                            .trim()
                            .toLowerCase();
                        const matchedAssignment = matchedAssignmentBySeat || assignmentOptions.find((option) => (
                            normalizedAssignedName && String(option.name || '').trim().toLowerCase() === normalizedAssignedName
                        ));
                        const assignedValue = matchedAssignment ? matchedAssignment.seatUid : '';
                        const scoreStyle = getScoreStyle(lead.score || 0);
                        const phone = lead.phone || lead.mobile;
                        const whatsapp = phone ? phone.replace(/[^0-9]/g, '') : '';
                        const lastTouchSource = lead.lastTouchAt || lead.lastContactedAt || lead.updatedAt || lead.createdAt;
                        const followUpStatus = getFollowUpStatus(lead);
                        const intent = sanitizeLeadField(lead.intentSummary, { suppressNameEcho: false });
                        const location = sanitizeLeadField(formatLocation(lead));
                        const leadShortId = lead.shortId ? String(lead.shortId).toUpperCase() : '-';
                        const dealsLabel = lead.openDealsCount
                            ? `${lead.openDealsCount} open - ${formatStage(lead.latestDealStage)}`
                            : '-';

                        return (
                            <tr key={lead.id} className="crm-table-row" onClick={() => handleRowClick(lead)}>
                                <td onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        className="crm-checkbox"
                                        checked={selectedIds.has(lead.id)}
                                        onChange={(e) => handleSelect(e, lead.id)}
                                    />
                                </td>
                                <td className="cell-main" style={getColumnStyle('name')}>
                                    <div className="crm-lead-name">
                                        <div className="crm-lead-title">{displayName}</div>
                                        <div className="crm-lead-badges">
                                            <span className={`crm-badge crm-badge-${lead.status || 'new'}`}>
                                                {statusLabels[lead.status] || 'New'}
                                            </span>
                                            <span className="crm-badge crm-badge-source">
                                                {sourceLabels[lead.source] || 'Other'}
                                            </span>
                                        </div>
                                    </div>
                                </td>
                                <td className="cell-muted" style={getColumnStyle('leadId')}>{leadShortId}</td>
                                <td style={getColumnStyle('nextFollowUp')}>
                                    <div className="crm-cell-stack">
                                        <div>{followUpStatus.display ?? formatDateTime(lead.nextFollowUpAt)}</div>
                                        <span className={`crm-pill ${followUpStatus.className}`}>{followUpStatus.label}</span>
                                    </div>
                                </td>
                                <td className="cell-secondary" style={getColumnStyle('lastTouch')}>
                                    <div className="crm-cell-stack">
                                        <div className="crm-text-strong">{getLastTouchLabel(lead)}</div>
                                        <div>{formatRelativeTime(lastTouchSource)}</div>
                                    </div>
                                </td>
                                <td style={getColumnStyle('intent')}>{intent}</td>
                                <td className="cell-secondary" style={getColumnStyle('location')}>{location}</td>
                                <td className="cell-secondary" style={getColumnStyle('deals')}>{dealsLabel}</td>
                                <td onClick={(e) => e.stopPropagation()} style={getColumnStyle('assignedTo')}>
                                    {assignmentOptions.length > 0 && typeof onAssign === 'function' ? (
                                        <select
                                            value={assignedValue}
                                            onChange={(e) => handleAssignChange(e, lead.id)}
                                            className="crm-select crm-select-sm"
                                        >
                                            {!matchedAssignment && (
                                                <option value="">Unassigned</option>
                                            )}
                                            {assignmentOptions.map((option) => (
                                                <option key={option.seatUid} value={option.seatUid}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        sanitizeLeadField(ownerName, { suppressNameEcho: false })
                                    )}
                                </td>
                                <td onClick={(e) => e.stopPropagation()} style={getColumnStyle('status')}>
                                    <select
                                        value={lead.status || 'new'}
                                        onChange={(e) => handleStatusChange(e, lead.id)}
                                        className="crm-select crm-select-sm"
                                    >
                                        {statusOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </td>
                                <td style={getColumnStyle('score')}>
                                    <span className="crm-badge" style={{ ...scoreStyle, minWidth: '32px', justifyContent: 'center' }}>
                                        {lead.score || 0}
                                    </span>
                                </td>
                                <td className="cell-actions" onClick={(e) => e.stopPropagation()} style={getColumnStyle('quickActions')}>
                                    <div className="crm-quick-actions crm-quick-actions-grid">
                                        {phone ? (
                                            <a
                                                className="crm-icon-btn"
                                                href={`tel:${phone}`}
                                                title="Call"
                                                onClick={(e) => handleQuickActionClick(e, lead, 'call')}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                                </svg>
                                            </a>
                                        ) : (
                                            <button className="crm-icon-btn" type="button" title="Call" disabled>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                                </svg>
                                            </button>
                                        )}
                                        {whatsapp ? (
                                            <a
                                                className="crm-icon-btn"
                                                href={`https://wa.me/${whatsapp}`}
                                                title="WhatsApp"
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={(e) => handleQuickActionClick(e, lead, 'whatsapp')}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 11.5a8.5 8.5 0 1 1-3.33-6.72" />
                                                    <path d="M22 12c0 4.97-4.03 9-9 9a8.96 8.96 0 0 1-4.65-1.3L3 21l1.32-4.05A8.96 8.96 0 0 1 3 12c0-4.97 4.03-9 9-9" />
                                                </svg>
                                            </a>
                                        ) : (
                                            <button className="crm-icon-btn" type="button" title="WhatsApp" disabled>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 11.5a8.5 8.5 0 1 1-3.33-6.72" />
                                                    <path d="M22 12c0 4.97-4.03 9-9 9a8.96 8.96 0 0 1-4.65-1.3L3 21l1.32-4.05A8.96 8.96 0 0 1 3 12c0-4.97 4.03-9 9-9" />
                                                </svg>
                                            </button>
                                        )}
                                        {lead.email ? (
                                            <a
                                                className="crm-icon-btn"
                                                href={`mailto:${lead.email}`}
                                                title="Email"
                                                onClick={(e) => handleQuickActionClick(e, lead, 'email')}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                                    <polyline points="22,6 12,13 2,6" />
                                                </svg>
                                            </a>
                                        ) : (
                                            <button className="crm-icon-btn" type="button" title="Email" disabled>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                                    <polyline points="22,6 12,13 2,6" />
                                                </svg>
                                            </button>
                                        )}
                                        <button className="crm-icon-btn" type="button" title="Add Task" onClick={(e) => handleAddTask(e, lead)}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M12 5v14" />
                                                <path d="M5 12h14" />
                                            </svg>
                                        </button>
                                        <button className="crm-icon-btn" type="button" title="Edit" onClick={(e) => handleEdit(e, lead)}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M12 20h9" />
                                                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                            </svg>
                                        </button>
                                        <button className="crm-icon-btn" type="button" title="Delete" onClick={(e) => handleDelete(e, lead)}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                <path d="M10 11v6" />
                                                <path d="M14 11v6" />
                                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                            </svg>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                </table>
            </div>
        </div>
    );
}

export default LeadTable;

